import React, { useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import {
  getSubscriptionDetail,
  storeSelectedSubscriptions
} from "../../sagas/actions";
import usePersistentState from "../../hooks/usePersistentState";

const breakpoints = {
  tablet: 768,
  phone: 480,
};

const FUTURE_BG = "#28A885";

const ISAFE_ROLES = new Set([
  "isafe_accountant",
  "isafe_moderator",
  "isafe_salesperson",
  "isafe_admin",
  "root",
]);
const SUBSCRIPTION_ROLES = new Set([
  "subscription_access",
  "subscription_accountant",
  "subscription_admin",
  "subscription_implementor",
  "subscription_salesperson",
]);

const Container = styled.div`
  font-family: Arial, sans-serif, verdana, helvetica;
  font-size: 90%;
  text-align: left;
  margin: 0;
  background: ${({ status }) => {
    if (status === "past") return "#E09826";
    if (status === "future") return "#28A885";
    return "#f8f8f8";
  }};
  color: ${({ status }) => (status === "future" ? "#ffffff" : "#2c4960")};
  padding: 1em;
  border-bottom: 1px solid #d5d5d5;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5em;
  }
`;

const TitleBlock = styled.div`
  font-weight: bold;

  span {
    display: block;
    font-size: 0.75em;
  }
`;

const HistoryWrapper = styled.div`
  font-size: 1em;
  font-weight: bold;
  white-space: nowrap;

  select {
    margin-left: 0.5em;
    min-width: 380px;
    padding: 6px 10px;
    font-size: 0.95em;
    line-height: 1.2;
    border: 1px solid #c7c7c7;
    border-radius: 6px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    white-space: normal;
    select {
      width: 100%;
      margin-left: 0;
      margin-top: 0.25em;
      min-width: 0;
    }
  }
`;

const formatMonthYear = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const formatFullDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const normalizeOrg = (name = "") => name.trim().toLowerCase();
const normalizeInvoiceNumber = (value) => String(value ?? "").trim();
const normalizeInvoiceId = (value) => String(value ?? "").trim();
const normalizeCustomerId = (value) => String(value ?? "").trim();
const normalizeCustomerCode = (value) => String(value ?? "").trim();

const toTime = (val, fallback = 0) => {
  if (!val) return fallback;
  const time = new Date(`${val}T00:00:00`).getTime();
  return Number.isNaN(time) ? fallback : time;
};

const makeSelectionKey = (row) => {
  const invoiceNumber = normalizeInvoiceNumber(row?.invoice_number);
  const customerId = normalizeCustomerId(row?.customer_id);
  const customerCode = normalizeCustomerCode(row?.customer_code);
  const identity = customerId || customerCode;
  if (invoiceNumber && identity) {
    return `${invoiceNumber}::${identity}`;
  }
  const invoiceId = String(row?.invoice_id ?? "").trim();
  if (invoiceId && identity) {
    return `${invoiceId}::${identity}`;
  }
  return "";
};

const findSubscriptionMatchByInvoice = (subs, external) => {
  if (!Array.isArray(subs) || !external) return "";
  const invoiceNumber = normalizeInvoiceNumber(external?.invoice_number);
  const invoiceId = normalizeInvoiceId(external?.invoice_id);
  const customerId = normalizeCustomerId(external?.customer_id);
  const customerCode = normalizeCustomerCode(external?.customer_code);
  const identity = customerId || customerCode;
  if (!invoiceNumber && !invoiceId) return "";

  const matches = subs.filter((sub) => {
    if (
      identity &&
      (normalizeCustomerId(sub?.customerId) || normalizeCustomerCode(sub?.customerCode)) !==
        identity
    ) {
      return false;
    }
    if (
      invoiceNumber &&
      normalizeInvoiceNumber(sub?.invoiceNumber) === invoiceNumber
    ) {
      return true;
    }
    if (invoiceId && normalizeInvoiceId(sub?.invoiceId) === invoiceId) {
      return true;
    }
    return false;
  });

  if (!matches.length) return "";
  const current = matches.find((sub) => sub.status === "current");
  if (current) return current.id;
  const future = matches.find((sub) => sub.status === "future");
  if (future) return future.id;
  return matches[0].id;
};

export default function SubscriptionInfo() {
  const dispatch = useDispatch();
  const roles = useSelector((s) => s.auth.user.roles || []);
  const hasISAFERole = roles.some((r) => ISAFE_ROLES.has(r.id));
  const becomeUser = useSelector((s) => s.auth.become_user);

  const user = useSelector((s) => s.auth.user || {});
  const externallySelected =
    useSelector((s) => s.auth.selected_subscriptions) || {};

  useEffect(() => {
    // While impersonating a user, preserve the existing subscription context
    // instead of re-fetching details with impersonated profile identifiers.
    if (becomeUser) {
      return;
    }

    if (
      user.invoice_id &&
      user.customer_id &&
      user.customer_code &&
      !hasISAFERole
    ) {
      dispatch(
        getSubscriptionDetail(
          user.invoice_id,
          user.customer_id,
          user.customer_code
        )
      );
    }
  }, [
    dispatch,
    becomeUser,
    user.invoice_id,
    user.customer_id,
    user.customer_code,
    hasISAFERole,
  ]);

  const invoiceList =
    useSelector((s) => s.auth.subscription_details?.invoiceDetail) || [];

  const subscriptions = useMemo(() => {
    const list = Array.isArray(invoiceList) ? [...invoiceList] : [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const determineStatus = (startIso, endIso) => {
      const startDate = new Date(`${startIso}T00:00:00`);
      const endDate = new Date(`${endIso}T23:59:59`);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return { status: "unknown", statusLabel: "Subscription" };
      }
      if (today < startDate) {
        return { status: "future", statusLabel: "Future Subscription", background: FUTURE_BG };
      }
      if (today > endDate) {
        return { status: "past", statusLabel: "Past Subscription" };
      }
      return { status: "current", statusLabel: "Current Subscription" };
    };

    list.sort((a, b) => {
      const startDiff = toTime(a.start_date, 0) - toTime(b.start_date, 0);
      if (startDiff !== 0) return startDiff;
      return toTime(a.end_date, 0) - toTime(b.end_date, 0);
    });

    return list.map((inv) => ({
      id: makeSelectionKey(inv),
      invoiceId: inv.invoice_id,
      invoiceNumber: inv.invoice_number,
      customerId: inv.customer_id,
      customerCode: inv.customer_code,
      org_name: inv.org_name,
      name: inv.product_desc,
      start: inv.start_date,
      end: inv.end_date,
      ...determineStatus(inv.start_date, inv.end_date),
      baseLabel: `${formatMonthYear(inv.start_date)} – ${formatMonthYear(
        inv.end_date
      )} (${inv.invoice_number})`,
    }));
  }, [invoiceList]);

  const defaultId = useMemo(() => {
    if (!subscriptions.length) return "";
    const current = subscriptions.find((sub) => sub.status === "current");
    if (current) return current.id;
    const future = subscriptions.find((sub) => sub.status === "future");
    if (future) return future.id;
    return subscriptions[subscriptions.length - 1].id;
  }, [subscriptions]);

  const userId = user?.user_id || "anonymous";
  const storageKey = `admincentral.subscription.selected.${userId}`;
  const [persistedSelectedId, setPersistedSelectedId] = usePersistentState(
    storageKey,
    ""
  );
  const lastDispatchedSelectionKeyRef = useRef("");
  const externalSelectionId = useMemo(
    () => findSubscriptionMatchByInvoice(subscriptions, externallySelected),
    [
      subscriptions,
      externallySelected?.invoice_number,
      externallySelected?.invoice_id,
      externallySelected?.customer_id,
      externallySelected?.customer_code,
    ]
  );
  const persistedSelectedExists = subscriptions.some(
    (s) => s.id === persistedSelectedId
  );
  const selectedId =
    externalSelectionId ||
    (persistedSelectedExists ? persistedSelectedId : "") ||
    defaultId;

  useEffect(() => {
  }, [
    externallySelected?.invoice_number,
    externallySelected?.invoice_id,
    externallySelected?.customer_id,
    externallySelected?.customer_code,
    externalSelectionId,
    persistedSelectedId,
    persistedSelectedExists,
    defaultId,
    selectedId,
    subscriptions.length,
  ]);

  const fullSelected = useMemo(
    () =>
      invoiceList?.find((inv) => makeSelectionKey(inv) === selectedId),
    [invoiceList, selectedId]
  );

  useEffect(() => {
    if (!fullSelected) return;

    const currentKey = makeSelectionKey(externallySelected);
    const nextKey = makeSelectionKey(fullSelected);

    // Avoid dispatching the same selection repeatedly, which can create
    // unnecessary render cycles on pages that mount SubscriptionInfo.
    if (!nextKey || nextKey === currentKey) {
      if (nextKey) {
        lastDispatchedSelectionKeyRef.current = nextKey;
      }
      return;
    }

    if (lastDispatchedSelectionKeyRef.current === nextKey) {
      return;
    }

    lastDispatchedSelectionKeyRef.current = nextKey;
    dispatch(storeSelectedSubscriptions(fullSelected));
  }, [
    dispatch,
    fullSelected,
    externallySelected?.invoice_id,
    externallySelected?.invoice_number,
    externallySelected?.customer_id,
  ]);

  const uiSelected =
    subscriptions?.find((s) => s.id === selectedId) || {};
  const selectedStatus = uiSelected.status;

  return (
    <Container status={selectedStatus}>
      <Header>
        <TitleBlock>
          {uiSelected.name || "No Subscription Selected"}
          <span>
            {uiSelected.start && uiSelected.end
              ? `${formatFullDate(uiSelected.start)} to ${formatFullDate(
                  uiSelected.end
                )}`
              : ""}
            {uiSelected.statusLabel
              ? ` • ${uiSelected.statusLabel}`
              : ""}
          </span>
        </TitleBlock>

        <HistoryWrapper>
          History:
          <select
            value={selectedId}
            onChange={(e) => {
              const nextId = e.target.value;
              setPersistedSelectedId(nextId);
              const nextSelected = invoiceList?.find(
                (inv) => makeSelectionKey(inv) === nextId
              );
              if (nextSelected) {
                dispatch(storeSelectedSubscriptions(nextSelected));
              }
            }}
          >
            {subscriptions?.map((sub) => {
              return (
                <option
                  key={sub.id}
                  value={sub.id}
                  style={
                    sub.status === "future"
                      ? { backgroundColor: FUTURE_BG, color: "#fff" }
                      : undefined
                  }
                >
                  {sub.statusLabel || "Subscription"} {sub.baseLabel}
                </option>
              );
            })}
          </select>
        </HistoryWrapper>
      </Header>
    </Container>
  );
}
